import { useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { NUM, pname } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ProductPicker from "@/components/ProductPicker";
import BarcodeScanner from "@/components/BarcodeScanner";
import BarcodeInput from "@/components/BarcodeInput";
import NewProductQuickDialog from "@/components/NewProductQuickDialog";
import { ScanLine, Package, X, Loader2, Send, ListPlus } from "lucide-react";
import { toast } from "sonner";

// Toplu sipariş talebi: barkod (tek tek okutma) veya stoktan arama ile liste oluştur → tek seferde talep et
export default function BulkOrderDialog({ open, onOpenChange, onDone }) {
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [np, setNp] = useState({ open: false, barcode: "" });   // bilinmeyen barkod → hızlı yeni ürün

  const add = (p) => setItems((list) => {
    const i = list.findIndex((x) => x.product.id === p.id);
    if (i >= 0) {   // tekrar okutuldu → adet +1, satır en üste
      const cur = list[i]; const rest = list.filter((_, idx) => idx !== i);
      toast.info(`${pname(p)} · adet ${Number(cur.qty || 0) + 1}`);
      return [{ ...cur, qty: Number(cur.qty || 0) + 1 }, ...rest];
    }
    return [{ product: p, qty: 1 }, ...list];   // en son eklenen EN ÜSTTE
  });
  const setQty = (id, v) => setItems((list) => list.map((x) => (x.product.id === id ? { ...x, qty: v } : x)));
  const rm = (id) => setItems((list) => list.filter((x) => x.product.id !== id));

  // Barkod çöz → listeye ekle/artır; sonucu kutuya döndür (yeşil/kırmızı şerit). Bulunamazsa UYARI + kutu temizlenir.
  const resolveScan = async (code) => {
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}&lite=true`);
      const exact = data.find((r) => [r.product.code, r.product.barcode, ...(r.product.barcodes || [])].some((b) => String(b || "").trim().toLowerCase() === String(code).trim().toLowerCase()));
      const hit = exact || (data.length === 1 ? data[0] : null);
      if (!hit) { toast.error(data.length ? `"${code}" birden fazla ürüne uyuyor — aramadan seçin` : `Barkod sistemde YOK: ${code}`); return { ok: false, label: data.length ? `Birden fazla: ${code}` : `Yok: ${code}` }; }
      add(hit.product);
      return { ok: true, label: pname(hit.product) };
    } catch (e) { toast.error(apiError(e)); return { ok: false, label: "Arama hatası" }; }
  };

  const submit = async () => {
    const valid = items.filter((x) => Number(x.qty) > 0);
    if (!valid.length) return toast.error("En az bir ürün ve miktar girin");
    setBusy(true);
    try {
      const { data } = await api.post("/orders/bulk", {
        note,
        items: valid.map((x) => ({ product_id: x.product.id, product_name: x.product.name, brand: x.product.brand || "", code: x.product.code || "", requested_qty: Number(x.qty), note: "" })),
      });
      toast.success(`${data.count} sipariş talebi iletildi`);
      setItems([]); setNote(""); onOpenChange(false); onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o && items.length && !window.confirm("Listede talep edilmemiş ürünler var. Kapatılsın mı?")) return; onOpenChange(o); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-order-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><ListPlus className="h-6 w-6 text-primary" />Toplu Sipariş Talebi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <BarcodeInput onResolve={resolveScan} onAddNew={(code) => setNp({ open: true, barcode: code })} placeholder="Barkod okut + Enter" autoFocus testid="bulk-barcode-input" className="w-full" />
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1"><ProductPicker label="Stoktan Ürün Ara / Ekle" size="default" variant="outline" className="w-full justify-start" onSelect={add} testid="bulk-pick" /></div>
                <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="bulk-scan-btn"><ScanLine className="h-4 w-4 mr-2" />Kamera</Button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground border border-dashed rounded-lg" data-testid="bulk-empty">
                <ScanLine className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="font-semibold">Barkod okutun veya ürün arayıp ekleyin</p>
                <p className="text-sm mt-1">Her ürün listenin en üstüne gelir; adetleri girip tek seferde iletin.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="bulk-items">
                {items.map((x) => {
                  const p = x.product;
                  const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
                  return (
                    <div key={p.id} className="flex items-center gap-3 rounded-xl border p-2.5 bg-card" data-testid={`bulk-item-${p.id}`}>
                      {imgs[0] ? <img src={fileUrl(imgs[0])} alt="" className="h-12 w-12 rounded-lg object-contain bg-white border shrink-0" /> : <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0"><Package className="h-5 w-5 text-muted-foreground/40" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm leading-snug break-words" data-testid={`bulk-name-${p.id}`}>{pname(p)}</div>
                        {(p.code || p.brand) && <div className="text-[11px] text-muted-foreground font-mono truncate">{[p.brand, p.code].filter(Boolean).join(" · ")}</div>}
                        <div className="text-xs text-muted-foreground">Stok: <span className="font-medium text-foreground tabular-nums">{NUM(p.quantity)} {p.unit}</span></div>
                      </div>
                      <div className="shrink-0 w-24">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Talep</div>
                        <Input type="number" min="0" className="h-9 text-right tabular-nums" placeholder="adet" value={x.qty === 0 ? "" : x.qty} onChange={(e) => setQty(p.id, e.target.value)} data-testid={`bulk-qty-${p.id}`} />
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => rm(p.id)} data-testid={`bulk-rm-${p.id}`}><X className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ortak Not (opsiyonel)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tüm taleplere eklenecek not…" data-testid="bulk-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-cancel">İptal</Button>
            <Button onClick={submit} disabled={busy || !items.length} data-testid="bulk-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Talepleri İlet ({items.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={resolveScan} continuous />
      <NewProductQuickDialog open={np.open} barcode={np.barcode} onOpenChange={(o) => setNp((s) => ({ ...s, open: o }))} onCreated={(p) => add(p)} />
    </>
  );
}
